import { useCallback, useEffect, useMemo, useState } from 'react'
import {
  trackNetworkStatusChanged,
  trackPwaPromptAccepted,
  trackPwaPromptDismissed,
  trackPwaPromptShown,
} from '@/lib/analytics'
import { APP_CONFIG } from '@/lib/config/constants'
import { logger } from '@/lib/utils/logger'

type InstallPromptOutcome = 'accepted' | 'dismissed'

interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[]
  prompt: () => Promise<void>
  userChoice: Promise<{ outcome: InstallPromptOutcome; platform: string }>
}

const DISMISS_KEY = APP_CONFIG.STORAGE_KEYS.PWA_INSTALL_DISMISSED
const INSTALL_KEY = APP_CONFIG.STORAGE_KEYS.PWA_INSTALLED

const supportsLocalStorage = () => {
  if (typeof window === 'undefined') return false
  try {
    const key = '__pwa_test__'
    window.localStorage.setItem(key, key)
    window.localStorage.removeItem(key)
    return true
  } catch {
    return false
  }
}

const storage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = supportsLocalStorage()
  ? window.localStorage
  : {
      getItem: () => null,
      setItem: () => void 0,
      removeItem: () => void 0,
    }

export function useInstallPrompt() {
  const [promptEvent, setPromptEvent] = useState<BeforeInstallPromptEvent | null>(null)
  const [isInstalled, setIsInstalled] = useState(() => storage.getItem(INSTALL_KEY) === 'true')
  const [isDismissed, setIsDismissed] = useState(() => storage.getItem(DISMISS_KEY) === 'true')

  useEffect(() => {
    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault()
      const castedEvent = event as BeforeInstallPromptEvent
      if (!isDismissed && !isInstalled) {
        setPromptEvent(castedEvent)
        trackPwaPromptShown()
      }
    }

    const handleAppInstalled = () => {
      setIsInstalled(true)
      storage.setItem(INSTALL_KEY, 'true')
    }

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
    window.addEventListener('appinstalled', handleAppInstalled)

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt)
      window.removeEventListener('appinstalled', handleAppInstalled)
    }
  }, [isDismissed, isInstalled])

  const dismissPrompt = useCallback(() => {
    setPromptEvent(null)
    setIsDismissed(true)
    storage.setItem(DISMISS_KEY, 'true')
    trackPwaPromptDismissed()
  }, [])

  const promptInstall = useCallback(async () => {
    if (!promptEvent) return
    try {
      await promptEvent.prompt()
      const result = await promptEvent.userChoice
      if (result.outcome === 'accepted') {
        setIsInstalled(true)
        storage.setItem(INSTALL_KEY, 'true')
        trackPwaPromptAccepted({ platform: result.platform })
      } else {
        trackPwaPromptDismissed({ platform: result.platform })
      }
      setPromptEvent(null)
    } catch (error) {
      logger.error('PWA install prompt failed', error)
    }
  }, [promptEvent])

  const canPrompt = useMemo(
    () => Boolean(promptEvent) && !isInstalled && !isDismissed,
    [promptEvent, isInstalled, isDismissed]
  )

  return {
    canPrompt,
    isInstalled,
    promptInstall,
    dismissPrompt,
  }
}

export function useNetworkStatus() {
  const [isOnline, setIsOnline] = useState(() =>
    typeof navigator !== 'undefined' ? navigator.onLine : true
  )

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      trackNetworkStatusChanged(true)
    }
    const handleOffline = () => {
      setIsOnline(false)
      trackNetworkStatusChanged(false)
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  return { isOnline }
}
